/**
 * The four risk tolerances a copier can be set to, and the copy that describes
 * them. One definition, read by the Start copying journey, the risk level guide
 * and the manage sheet, so all three always describe a level the same way.
 *
 * The lot multiplier that reaches the API as the copier's riskValue is NOT a
 * property of the level — it depends on the slave's starting balance as well,
 * so a $200K account on Standard copies at 2x where a $100K one copies at 1x.
 * MULTIPLIERS below is that grid; read it through multFor().
 *
 * A level with no multiplier for an account size is not offered on it at all,
 * which is how Conservative stays a $200K-and-up option — the grid is the one
 * place that gates a level, rather than a separate minimum.
 *
 * The figures and wording are the Figma copy for the journey's second step.
 */

/* Lot multiplier by the slave's starting balance and the level picked. Straight
   from the desk's table; a null is a level that size of account may not use.
 *
 *            conservative  standard  moderate  aggressive
 *   100000              -         1         2           3
 *   200000              1         2         4           6
 *   300000            1.5         3         6           9
 *   400000              2         4         8          12
 */
const MULTIPLIERS = {
  100000: { conservative: null, standard: 1, moderate: 2, aggressive: 3 },
  200000: { conservative: 1,    standard: 2, moderate: 4, aggressive: 6 },
  300000: { conservative: 1.5,  standard: 3, moderate: 6, aggressive: 9 },
  400000: { conservative: 2,    standard: 4, moderate: 8, aggressive: 12 },
};

const TIERS = Object.keys(MULTIPLIERS).map(Number).sort((a, b) => a - b);

/* The row of the grid an account reads from. Balances land on these tiers in
   practice, but an odd one rounds DOWN to the tier below rather than up — a
   $250K account copies at the $200K sizes, never the $300K ones — and anything
   past the top of the table is held at it. A missing or unparseable balance
   reads as the smallest tier, so an unknown account is never handed the larger
   multipliers on a guess. */
function tierFor(deposit) {
  const size = Number(deposit) || 0;
  let tier = TIERS[0];
  for (const t of TIERS) if (size >= t) tier = t;
  return tier;
}

/* The multiplier this level copies at on an account of this size, or null if
   the level isn't offered there. */
export function multFor(levelId, deposit) {
  return MULTIPLIERS[tierFor(deposit)]?.[levelId] ?? null;
}

export const RISK_LEVELS = [
  {
    id: 'conservative',
    label: 'Conservative',
    // The phone form: four full names don't fit across a slider at 375px.
    short: 'CON',
    dailyTarget: '$50-75/day',
    maxDailyLoss: '$500',
    // Conservative is the one level whose copy doesn't quote a full-loss day
    // count — it speaks to headroom instead, and inventing a number for a
    // risk disclosure is not ours to do.
    fullLossDays: null,
    blurb: 'Built for a lower-risk approach with more room to absorb losing periods.',
    guide: 'The steadiest setting. Smaller positions mean smaller daily swings and the most room to sit through a losing stretch without approaching your drawdown limit.',
    note: 'Only available for $200K accounts.',
  },
  {
    id: 'standard',
    label: 'Standard',
    // The phone form: four full names don't fit across a slider at 375px.
    short: 'STD',
    dailyTarget: '$50-75/day',
    maxDailyLoss: '$500',
    fullLossDays: 'roughly 7',
    blurb: 'A lower-risk setting with roughly 7 full-loss days before reaching max drawdown.',
    guide: 'The default setting, and where most accounts start. It copies the master lot for lot, so your account tracks the strategy as closely as it can.',
  },
  {
    id: 'moderate',
    label: 'Moderate',
    // The phone form: four full names don't fit across a slider at 375px.
    short: 'MOD',
    dailyTarget: '$100-150/day',
    maxDailyLoss: '$1,000',
    fullLossDays: 'roughly 3-4',
    blurb: 'A balanced setting for higher return potential, with roughly 3-4 full-loss days before reaching max drawdown.',
    guide: 'Twice the size of Standard, for a faster build at the cost of half the room to absorb a losing run.',
  },
  {
    id: 'aggressive',
    label: 'Aggressive',
    // The phone form: four full names don't fit across a slider at 375px.
    short: 'AGG',
    dailyTarget: '$200-300/day',
    maxDailyLoss: '$2,000',
    fullLossDays: 'roughly 2-3',
    blurb: 'Built for faster return potential, with roughly 2-3 full-loss days before reaching max drawdown.',
    guide: 'The largest positions on offer. The fastest way to reach a target, and the fastest way to reach your drawdown limit — a handful of full-loss days is all it takes.',
  },
];

/* The levels an account of this size may pick from — the ones the grid gives it
   a multiplier for. A $200K account and up gets all four; a $100K account gets
   the three above Conservative. An unknown deposit reads as the smallest tier,
   so a missing figure can never quietly offer a level the account isn't
   entitled to. */
export function levelsFor(deposit) {
  return RISK_LEVELS.filter((level) => multFor(level.id, deposit) != null);
}

export const levelById = (id) => RISK_LEVELS.find((l) => l.id === id) || null;

/* The level a stored riskValue corresponds to — the manage sheet opens on a
   copier that already has a multiplier. Measured against the grid row for this
   account, since the same 2x is Standard on a $200K account and Moderate on a
   $100K one. An exact match where there is one, otherwise the nearest level, so
   a hand-set 1.4x still lands somewhere sensible rather than nowhere. */
export function levelForMult(mult, deposit) {
  const options = levelsFor(deposit);
  if (!options.length) return null;
  const value = Number(mult);
  if (!Number.isFinite(value)) return options[0];
  const distance = (level) => Math.abs(multFor(level.id, deposit) - value);
  return options.reduce((best, level) => (distance(level) < distance(best) ? level : best));
}

export default RISK_LEVELS;
